import { HFlow, Modal, ModalBody, ModalFooter, ModalFooterButton, ModalHeader, Text, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Form, FormRenderProps, SubmitButton } from 'components/form'
import { SalaSelectField } from 'components/form/field/select/SalaSelectField'
import { TipoAtendimentoPainelChamadasRadioGroupField } from 'components/form/field/TipoAtendimentoPainelChamadasRadioGroupField'
import { useChamarPainelMutation } from 'graphql/hooks.generated'
import { useMemo } from 'react'
import { metaPath } from 'util/metaPath'

import {
  AtendimentoPainelChamadaModel,
  ChamarCidadaoPainelChamadaModel,
  SalaUnidadeSaude,
  TipoAtendimentoPainelChamadaEnum,
} from './model-painelChamada'
import { chamarCidadaoPainelFormValidator as validator } from './validator-chamarCidadaoPainel'

interface ChamarCidadaoModalFormProps {
  atendimento: AtendimentoPainelChamadaModel
  salas: ReadonlyArray<SalaUnidadeSaude>
  initialValues: Partial<ChamarCidadaoPainelChamadaModel>
  onClose: () => void
  onSuccess: (salaId: ID, tipoAtendimento: TipoAtendimentoPainelChamadaEnum) => void
}

const meta = metaPath<ChamarCidadaoPainelChamadaModel>()

export const ChamarCidadaoModalForm = (props: Readonly<ChamarCidadaoModalFormProps>) => {
  const alert = useAlert()

  const { atendimento, salas, initialValues, onClose: handleClose, onSuccess } = props
  const [chamarPainel] = useChamarPainelMutation()

  const salasOptions = useMemo(() => salas.map((sala) => ({ id: sala.id, label: sala.nome })), [salas])

  const nomeCidadao = atendimento.cidadao.nomeSocial ?? atendimento.cidadao.nome

  const formInitialValues = useMemo((): ChamarCidadaoPainelChamadaModel => {
    return {
      atendimentoId: atendimento.id,
      tipoAtendimento: initialValues?.tipoAtendimento ?? TipoAtendimentoPainelChamadaEnum.ATENDIMENTO,
      sala: initialValues?.sala ?? salasOptions[0],
    }
  }, [atendimento.id, initialValues?.tipoAtendimento, initialValues?.sala, salasOptions])

  const handleSubmit = (values: ChamarCidadaoPainelChamadaModel) => {
    return chamarPainel({
      variables: {
        input: {
          atendimentoId: values.atendimentoId,
          tipoAtendimentoPainelChamada: values.tipoAtendimento,
          salaId: values.sala.id,
        },
      },
    }).then(() => {
      onSuccess(values.sala.id, values.tipoAtendimento)
      handleClose()
      alert('success', 'Cidadão chamado com sucesso no painel de chamadas.')
    })
  }

  const renderForm = (formProps: FormRenderProps<ChamarCidadaoPainelChamadaModel>) => {
    const { handleSubmit, submitting } = formProps

    return (
      <Modal open onClose={handleClose} style={styles.modal}>
        <ModalHeader
          icon='desktopOutline'
          title='Painel de chamada'
          subtitle='O painel de chamadas exibirá as informações abaixo'
        />
        <ModalBody>
          <VFlow vSpacing={1}>
            <VFlow vSpacing={0}>
              <Text variant='h5'>Nome do cidadão</Text>
              <Text>{nomeCidadao}</Text>
            </VFlow>

            <TipoAtendimentoPainelChamadasRadioGroupField name={meta.tipoAtendimento} disabled={submitting} required />

            <SalaSelectField name={meta.sala} items={salasOptions} disabled={submitting} required />
          </VFlow>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <ModalFooterButton onClick={handleClose}>Cancelar</ModalFooterButton>
            <SubmitButton handleSubmit={handleSubmit} isModalFooterButton>
              Chamar
            </SubmitButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<ChamarCidadaoPainelChamadaModel>
      initialValues={formInitialValues}
      render={renderForm}
      onSubmit={handleSubmit}
      validate={validator}
    />
  )
}

const styles = {
  modal: {
    width: '32.5rem',
  },
}
